import { useCallback, useEffect, useRef, useState } from 'react'
import { clearErrorFromBorderColor, scrollIntoView, showErrorAsBorderColor, SurveyQuestionType } from '../../models/Survey'
import { type ISurveySection } from '../../models/SurveyTemplate'
import { type UseSurveyFeatureStepperReturnValue, type ISection, type ShouldRenderSectionFn, BranchingOperation, type HandleChangeFn, type OnSubmitFn, type SectionItem, type HandleBackFn, type HandleNextFn, type SectionResponseItem } from './components/SurveyFeatureStepper/types'
import { bulkParseModdedSectionIdForSectionItems, findOrigin, MULTIPLE_CHOICE_SEPERATOR, transformSections } from './components/SurveyFeatureStepper/utils'
import { useStepper } from './components/SurveyFeatureStepper/useStepper'
import { useFormContext } from 'react-hook-form'
import { debounce } from 'lodash'

/**
 * Custom hook to manage survey stepper functionality with branching logic, progress tracking,
 * validation, and dynamic rendering of sections and items.
 *
 * This hook:
 * - Transforms raw survey section data into a nested, hierarchical format
 * - Tracks progress and user responses across steps
 * - Validates required survey questions
 * - Manages rendering logic for conditionally visible (branched) sections
 */
export const useSurveyFeatureStepper = (rawSections: ISurveySection[] | undefined, onSubmit: OnSubmitFn): UseSurveyFeatureStepperReturnValue => {
  // Local state to store transformed sections
  const [sections, setSections] = useState<ISection[]>([])
  const [invalidItemIDs, setInvalidItemIDs] = useState<string[]>([])
  const [triggerPulse, setTriggerPulse] = useState<boolean>(false)

  // Refs to keep track of rendered sections and user responses without triggering re-renders
  const renderedSectionsRef = useRef<Map<string, boolean>>(new Map<string, boolean>())
  const responseItemsRef = useRef <Record<ISection['surveySectionId'], SectionResponseItem[]>>({})

  const { handleSubmit } = useFormContext()
  const { step, setStep, setProgress } = useStepper()

  /**
   * Transforms raw incoming sections data into a structured tree format using `transformSections`.
   */
  useEffect(() => {
    if (rawSections) {
      const transformed = transformSections(rawSections)
      setSections(transformed)
    }
  }, [rawSections])

  /**
   * Records survey items that have been rendered for the current step,
   * collecting user responses from the visible section tree.
   */
  const recordRenderedSectionItems = (): void => {
    const rootSection = sections[step]
    const items = getBranchItems(rootSection)

    responseItemsRef.current = { ...responseItemsRef.current, [rootSection.surveySectionId]: items }
  }

  /**
   * Recursively collects survey items for a section and its visible children.
   * Converts user inputs into response item format and handles multiselect splitting.
   */
  const getBranchItems = (section: ISection): SectionResponseItem[] => {
    const isRendered = renderedSectionsRef.current.has(section.surveySectionId)

    let surveyResponseItems = section.surveyItems.map(
      item => {
        const { itemId, value, revision, itemType } = item

        let answer: string | string[] | undefined = value === '' ? undefined : value

        if (itemType === SurveyQuestionType.MultiSelect && value !== '') {
          answer = value.split(MULTIPLE_CHOICE_SEPERATOR)
        }

        return { answer: isRendered ? answer : undefined, itemId, revision, instructorId: section.instructorId, surveySectionId: section.surveySectionId, surveySectionRevision: revision }
      }
    ) as SectionResponseItem[]

    for (const child of section.children) {
      const branchItems = getBranchItems(child)
      surveyResponseItems = [...surveyResponseItems, ...branchItems]
    }

    return surveyResponseItems
  }

  /**
   * Recursively validates required fields for a section and all visible child sections.
   * Returns lists of valid and invalid item IDs.
   */
  const validateBranchItems = (section: ISection): { invalidItems: string[], validItems: string[] } => {
    if (!renderedSectionsRef.current.has(section.surveySectionId)) return { invalidItems: [], validItems: [] }

    let invalidItems: string[] = []
    let validItems: string[] = []

    section.surveyItems.forEach(
      item => {
        if (item.isRequired && item.value === '') invalidItems.push(item.itemId)
        else validItems.push(item.itemId)
      }
    )

    for (const child of section.children) {
      const branchInfo = validateBranchItems(child)
      invalidItems = [...invalidItems, ...branchInfo.invalidItems]
      validItems = [...validItems, ...branchInfo.validItems]
    }

    return {
      invalidItems,
      validItems
    }
  }

  /**
   * Recursively calculates total and answered questions for a section and all its children.
   */
  const evaluateBranch = (section: ISection): { total: number, answered: number } => {
    let total = 0
    let answered = 0

    if (section.parentSurveySectionId && !renderedSectionsRef.current.has(section.surveySectionId)) { return { total, answered } }

    section.surveyItems.forEach(
      item => {
        total++
        if (item.value !== '') answered++
      }
    )

    section.children.forEach(child => {
      const childInfo = evaluateBranch(child)
      total += childInfo.total
      answered += childInfo.answered
    })

    return { total, answered }
  }

  /**
   * Evaluates progress across all root sections and updates progress.
   * Required for the progress bar state.
   */
  const evaluteProgress = (sections: ISection[]): void => {
    const progress = sections.reduce(
      (prev, root: ISection) => {
        const { total, answered } = evaluateBranch(root)

        const updatedProgress = { total: prev.total + total, answered: prev.answered += answered }
        return updatedProgress
      }, { total: 0, answered: 0 }
    )

    setProgress(progress)
  }

  /**
   * Debounced version of progress evaluation to prevent excessive computation on change.
   */
  const debouncedEvaluteProgress = useCallback(
    debounce((sections: ISection[]) => {
      evaluteProgress(sections)
    }, 1000), // 500ms debounce delay
    []
  )

  /**
   * Flattens and parses all response items for submission for backend.
   */
  const getResponseItems = (): SectionResponseItem[] => {
    return Object.values(responseItemsRef.current).reduce((p, c) => [...p, ...bulkParseModdedSectionIdForSectionItems(c)], [])
  }

  // Navigation Handlers

  /**
   * Moves the user one step back in the stepper.
   * Handles any state changes required when going one step back.
   */
  const handleBack: HandleBackFn = () => {
    setTriggerPulse(false)
    setStep(prev => prev - 1)
  }

  /**
   * Validates current step, shows errors if present, or progresses to next step.
   * On final step, submits the survey responses via `onSubmit`.
   */
  const handleNext: HandleNextFn = () => {
    const { validItems, invalidItems } = validateBranchItems(sections[step])

    setTriggerPulse(false)

    if (invalidItems.length === 0) {
      recordRenderedSectionItems()

      if (step < sections.length - 1) { setStep(prev => prev + 1) }

      if (step === sections.length - 1) {
        const onSubmitPromise = handleSubmit(async () =>
          await onSubmit(getResponseItems())
        )()

        onSubmitPromise.then(() => {}).catch(console.error)
      }

      return
    }

    // Show error styles for invalid items
    invalidItems.forEach(
      (itemId, index) => {
        // We have survey items with same itemId in multiple sections for multiple instructors there for we need to combine instructorID for uniqueness
        const element = document.getElementById(`${itemId}${sections[step].instructorId ? sections[step].instructorId : ''}`)
        if (!element) {
          console.error('Found a question with id {' + itemId + '} without any HTMLElement')
          return
        }

        showErrorAsBorderColor(element)
        if (index === 0) scrollIntoView(element)
      }
    )

    // Clear error styles from valid items
    validItems.forEach(
      itemId => {
        const element = document.getElementById(`${itemId}${sections[step].instructorId ? sections[step].instructorId : ''}`)
        if (!element) {
          console.error('Found a question with id {' + itemId + '} without any HTMLElement')
          return
        }
        clearErrorFromBorderColor(element)
      }
    )

    setInvalidItemIDs(invalidItems)
  }

  /**
   * Determines if a section should be rendered based on branching logic.
   */
  const shouldRenderSection: ShouldRenderSectionFn = (root, section) => {
    if (!section.origin?.itemId) return true

    const rootSection = sections.find(_section => _section.surveySectionId === root.surveySectionId)

    if (!rootSection) {
      console.error('Found a section without a root section: ' + section.surveySectionId)
      return false
    }

    const originItem = findOrigin(rootSection, section.origin?.itemId)

    if (!originItem) {
      console.error('Found faulty data for origin: ' + section.surveySectionId)
      return false
    }

    const { itemType, branches, value } = originItem

    const branchingValues = branches.filter(b => b.sectionId === section.surveySectionId).map(branch => branch.branchingValue.toLowerCase().trim())
    if (branchingValues.length === 0) {
      console.error('Found a section with origin but no branch: ' + section.surveySectionId)

      return false
    }

    // Evaluate branching condition
    if (section.origin.operation.toLowerCase() === BranchingOperation.IS_EMPTY) return value === ''
    if (section.origin.operation.toLowerCase() === BranchingOperation.IS_NOT_EMPTY) return (value !== '')

    if (section.origin.operation.toLowerCase() === BranchingOperation.IS) {
      const formattedValue = value.toLowerCase().trim()

      if (itemType === SurveyQuestionType.LinearScale) return branchingValues.includes(formattedValue)
      if (itemType === SurveyQuestionType.MultipleChoice) return branchingValues.includes(formattedValue)
      if (itemType === SurveyQuestionType.MultiSelect) return !!branchingValues.find(branchingValue => formattedValue.split(MULTIPLE_CHOICE_SEPERATOR).includes(branchingValue))
    }

    return false
  }

  /**
   * Updates value of a specific survey item and triggers progress evaluation.
   */
  const handleChange: HandleChangeFn = (value, sectionId, itemId) => {
    const updateSection = (sections: ISection[]): ISection[] => {
      return sections.map(_section => {
        if (_section.surveySectionId === sectionId) {
          const surveyItems = _section.surveyItems.map(_item => {
            if (_item.itemId !== itemId) return _item

            const valueStr = typeof value === 'string' ? value : value.join(MULTIPLE_CHOICE_SEPERATOR)

            return { ..._item, value: valueStr }
          })

          return { ..._section, surveyItems }
        }

        if (_section.children.length > 0) {
          return { ..._section, children: updateSection(_section.children) }
        }

        return _section
      })
    }

    setTriggerPulse(true)
    setSections(prev => {
      const updatedSections = updateSection(prev)
      debouncedEvaluteProgress(updatedSections)

      return updatedSections
    })
  }

  return { sections, renderedSectionsRef, invalidItemIDs, handleChange, handleBack, handleNext, shouldRenderSection, triggerPulse }
}
